/**
 * React hook wiring the web app's send + receive file-transfer UI state —
 * mirrors the composition `apps/extension/src/sidepanel/SidePanel.tsx` does
 * across `notifyFileCtrlReceived` + `useReceiveFileTransfers` (extension),
 * collapsed into one hook since the web app has a single page (no
 * cross-context push messages to compose).
 *
 * Registers ONE `FileCtrlHandler` with `fileCtrl.ts` (last-registration-wins
 * seam) that feeds BOTH the sender's live-loop control registry
 * (`notifyFileCtrlReceived`) and this hook's own offer/progress/outcome
 * state — both tolerate ctrl kinds meant for the other role, mirroring the
 * extension's composition.
 *
 * Runs the §5.1/§6.1 app-open resume triggers + the OPFS startup GC once per
 * pairing (gated on `enabled` — see the param doc).
 *
 * UNPAIRED CONTRACT: with no pairing there is nothing to resume, so the
 * resume/GC trigger below is arg-gated on `enabled` (MainPage passes
 * `paired`). The remaining effects are cheap local wiring — a pending-offer
 * read and two subscriptions — and run regardless, exactly as they did when
 * the build gate was ON, which was the shipping default.
 *
 * SECURITY: never render file content — only name/size/sender label
 * (already trusted the same way the extension's cards are) — security.md.
 */

import type {
  ReceiveFileOffer,
  ReceiveFileOutcome,
  ReceiveFileProgress,
  SendFileDevice,
} from "@reflecto/client-ui";
import type { FileCtrl } from "@reflecto/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { useStorage } from "../hooks/useStorage";
import { messages } from "../i18n";
import { logWarn } from "../lib/log";
import { ftErrorIdentity } from "./errorIdentity";
import { registerFileCtrlHandler, sendFileCtrl, unregisterFileCtrlHandler } from "./fileCtrl";
import { reportOfferResponded } from "./ftAnalytics";
import { listOffers, removeOffer } from "./offerStore";
import { makePreAcceptTombstone, saveReceiverState } from "./receiverState";
import {
  cancelActiveReceive,
  gcOrphanTransfers,
  handleInboundReceiveCtrl,
  resumePendingReceivesOnAppOpen,
  startReceive,
  subscribeReceiveTransferEvents,
} from "./receiveTransfer.web";
import { notifyFileCtrlReceived, resumePendingSendsOnAppOpen } from "./senderTransfer.web";

export interface UseFileTransfersResult {
  devices: SendFileDevice[];
  offers: ReceiveFileOffer[];
  progress?: ReceiveFileProgress;
  outcome?: ReceiveFileOutcome;
  onAccept(transferId: string): void;
  onDecline(transferId: string): void;
  onCancelReceive(transferId: string): void;
}

/**
 * Internal offer state — deliberately WITHOUT `senderLabel`. The label is
 * derived at RENDER time (see the return block) so a late-loading
 * `androidDeviceName` upgrades offers that arrived while it was still
 * `undefined`, instead of baking a stale fallback into state forever.
 */
type PendingOfferEntry = Omit<ReceiveFileOffer, "senderLabel">;

/**
 * `enabled` gates whether the resume/GC triggers should run at all — mirrors
 * the extension's panel-mount trigger. MainPage passes `paired`: the triggers
 * are only meaningful once a device is paired.
 */
export function useFileTransfers(enabled: boolean | undefined): UseFileTransfersResult {
  const { value: androidDeviceId } = useStorage("androidDeviceId");
  const { value: androidDeviceName } = useStorage("androidDeviceName");

  const [offers, setOffers] = useState<PendingOfferEntry[]>([]);
  const [progress, setProgress] = useState<ReceiveFileProgress | undefined>(undefined);
  const [outcome, setOutcome] = useState<ReceiveFileOutcome | undefined>(undefined);
  // Progress/done events carry only a transferId — remember the file name and size
  // from the accepted offer so the receiving rows can keep showing them.
  const fileMeta = useRef(new Map<string, { name: string; size: number }>());
  // #808 item 6 — transferIds we've already sent an `offer_received` receipt for,
  // so a /sync-replayed offer doesn't re-ack. Session-scoped (best-effort).
  const ackedOffers = useRef(new Set<string>());

  // §5.1/§6.1 app-open resume triggers + startup GC. Deps are [enabled], so
  // this re-fires on an unpair→repair transition in the same tab (not strictly
  // "once") — safe by design: each scan takes the per-transfer Web Locks
  // single-writer lock (senderTransfer.web.ts / receiveTransfer.web.ts
  // `acquireLock`) so a re-fire never double-drives a live transfer, and
  // records cannot outlive a pairing (`clearLocalState` wipes
  // ftSenderStates/ftReceiverStates + the OPFS ft/ dir on unpair), so the
  // re-paired scan sees an empty store — a correct fresh start.
  useEffect(() => {
    if (!enabled) return;
    void resumePendingSendsOnAppOpen();
    void resumePendingReceivesOnAppOpen();
    void gcOrphanTransfers();
  }, [enabled]);

  // Snapshot any offers that arrived before this hook mounted (covers a
  // reload while an offer is pending) — mirrors the extension's
  // GET_PENDING_FILE_OFFERS fetch-on-mount.
  useEffect(() => {
    setOffers(
      listOffers().map((o) => ({
        transferId: o.transferId,
        fileName: o.manifest.files[0].name,
        fileSize: o.manifest.files[0].size,
      }))
    );
  }, []);

  const handleFileCtrl = useCallback((senderDeviceId: string, ctrl: FileCtrl) => {
    if (ctrl.kind === "offer") {
      const { transferId } = ctrl.manifest;
      // #808 item 6 — auto-post a delivery receipt the instant the offer lands,
      // before the user decides, so the sender's card can leave "Delivering…".
      // Best-effort + once per transferId (a /sync replay must not re-ack).
      if (!ackedOffers.current.has(transferId)) {
        ackedOffers.current.add(transferId);
        void sendFileCtrl(senderDeviceId, { kind: "offer_received", transferId }).catch(() => {});
      }
      setOffers((prev) =>
        prev.some((o) => o.transferId === transferId)
          ? prev
          : [
              ...prev,
              {
                transferId,
                fileName: ctrl.manifest.files[0].name,
                fileSize: ctrl.manifest.files[0].size,
              },
            ]
      );
    } else if (ctrl.kind === "cancel") {
      setOffers((prev) => prev.filter((o) => o.transferId !== ctrl.transferId));
    }
    // Fan the SAME ctrl to both roles' live-state routers (a transferId is
    // one-directional, so exactly one of them acts; the other finds no
    // matching registry entry/record and drops per §8.2/§8.3) — mirrors the
    // extension SidePanel's notifyFileCtrlReceived + offscreen
    // handleInboundCtrl composition, collapsed into one page here.
    notifyFileCtrlReceived(senderDeviceId, ctrl);
    handleInboundReceiveCtrl(ctrl);
  }, []);

  // Register ONCE — last-registration-wins seam (see fileCtrl.ts's module doc).
  useEffect(() => {
    registerFileCtrlHandler(handleFileCtrl);
    return () => unregisterFileCtrlHandler();
  }, [handleFileCtrl]);

  // Drain receiveTransfer.web.ts's progress/paused/done events into this hook's UI state.
  useEffect(() => {
    return subscribeReceiveTransferEvents((event) => {
      const meta = fileMeta.current.get(event.transferId);
      if (event.type === "progress") {
        setProgress({
          transferId: event.transferId,
          fileName: meta?.name ?? "",
          fileSize: meta?.size,
          partIndex: event.partIndex,
          partCount: event.partCount,
        });
      } else if (event.type === "done") {
        setProgress((prev) => (prev?.transferId === event.transferId ? undefined : prev));
        setOutcome({
          transferId: event.transferId,
          fileName: meta?.name ?? "",
          fileSize: meta?.size,
          outcome: event.outcome,
          // TASK-30 — was previously dropped here even though receiveTransfer.web.ts's "done"
          // event already carries it; the ReceiveFileCard's failed-outcome text was generic
          // regardless of the actual FtErrorCode until this was threaded through.
          errorCode: event.errorCode,
        });
        fileMeta.current.delete(event.transferId);
      }
    });
  }, []);

  const onAccept = useCallback(
    (transferId: string) => {
      const offer = offers.find((o) => o.transferId === transferId);
      setOffers((prev) => prev.filter((o) => o.transferId !== transferId));
      // Re-look-up the manifest from the durable offer registry (not just the UI's shadow copy) —
      // startReceive needs the full TransferManifest, which the UI-facing ReceiveFileOffer doesn't carry.
      const pending = listOffers().find((o) => o.transferId === transferId);
      if (!pending) {
        setOutcome({
          transferId,
          fileName: offer?.fileName ?? "",
          fileSize: offer?.fileSize,
          outcome: "failed",
        });
        return;
      }
      if (offer) fileMeta.current.set(transferId, { name: offer.fileName, size: offer.fileSize });
      removeOffer(transferId);
      // Intent event — a human accepted a real offer, captured from this UI-driven call.
      reportOfferResponded(true);
      startReceive(transferId, pending.manifest.senderDeviceId, pending.manifest);
    },
    [offers]
  );

  const onDecline = useCallback((transferId: string) => {
    setOffers((prev) => prev.filter((o) => o.transferId !== transferId));
    const pending = listOffers().find((o) => o.transferId === transferId);
    removeOffer(transferId);
    if (!pending) return;
    reportOfferResponded(false);
    const senderDeviceId = pending.manifest.senderDeviceId;
    // §8.3: persist the "declined" tombstone BEFORE the decline ctrl (mirrors
    // the extension's handleDeclineFileTransfer), so a crash between the two
    // — or a later offer replay via /sync — can never re-notify a decided
    // offer; fileCtrl.ts's durable dedup drops the replay against it. An
    // unsent ctrl is healed by the sender's ACCEPT_TIMEOUT_MS no-answer path.
    void (async () => {
      // Audit fix (#818 Part 4 sweep): the ctrl swallow below IS justified (the sender's
      // ACCEPT_TIMEOUT_MS heals an unsent decline) — but NOTHING heals a failed tombstone write,
      // and a missing tombstone lets a replayed offer re-notify a decision the user already made.
      await saveReceiverState(makePreAcceptTombstone(transferId, senderDeviceId, "declined")).catch(
        (err) => logWarn("filetransfer", "decline_tombstone_write_failed", ftErrorIdentity(err))
      );
      await sendFileCtrl(senderDeviceId, { kind: "decline", transferId, reason: "user" }).catch(
        () => {}
      );
    })();
  }, []);

  const onCancelReceive = useCallback((transferId: string) => {
    void cancelActiveReceive(transferId);
  }, []);

  // Display labels are derived HERE, at render time — not baked into state
  // when an offer arrives — so `androidDeviceName` resolving after mount
  // (useStorage starts `undefined`) retroactively fixes pre-existing offers,
  // and the no-name fallback comes from the i18n catalog (mirrors the
  // extension's `androidDeviceName ?? t("sendFilePhoneFallbackName")`), never
  // hardcoded English.
  const partnerLabel = androidDeviceName ?? messages.fileTransfer.sendPhoneFallbackName;
  const labeledOffers: ReceiveFileOffer[] = offers.map((o) => ({
    ...o,
    senderLabel: partnerLabel,
  }));

  const devices: SendFileDevice[] = androidDeviceId
    ? [{ id: androidDeviceId, name: partnerLabel }]
    : [];

  return {
    devices,
    offers: labeledOffers,
    progress,
    outcome,
    onAccept,
    onDecline,
    onCancelReceive,
  };
}
